import { useState } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  QrCode, Moon, BarChart3, Package, Truck, CalendarRange, Sparkles, Layers,
  Rocket, Calendar, Trash2, AlertTriangle, Shield, KeyRound, ChevronRight, ChevronDown,
  ScrollText
} from 'lucide-react';

const RELEASES = [
  {
    version: 'v2.5.0',
    date: '19 April 2026',
    tag: 'latest',
    summary: 'QR passwordless login, dark/light theme, picker system, capacity heatmap, full backend modularisation.',
    sections: [
      {
        icon: <QrCode className="h-4 w-4" />,
        title: 'QR Code Passwordless Login',
        color: 'var(--amber)',
        bg: 'var(--amber-glow)',
        items: [
          'Scan the QR code on the login page with your phone to sign in on desktop — no typing needed',
          'First scan registers your phone (one-time password entry)',
          'All subsequent scans are instant & passwordless',
          'Manage registered devices in Settings with one-tap revoke',
          'HMAC-SHA256 signed QR URLs — tamper-proof',
        ],
      },
      {
        icon: <Moon className="h-4 w-4" />,
        title: 'Dark / Light Theme Toggle',
        color: 'var(--blue)',
        bg: 'var(--blue-dim)',
        items: [
          'Switch between Dark mode (RouteOps) and Light mode in Settings',
          'Preference saved and persists across sessions',
        ],
      },
      {
        icon: <BarChart3 className="h-4 w-4" />,
        title: 'Picker Performance Reporting',
        color: 'var(--green)',
        bg: 'var(--green-dim)',
        items: [
          'New Reporting page in RouteOps sidebar',
          'See which orders each picker handled, how long each pick took, and daily averages',
          'Filter by date range for historical analysis',
        ],
      },
      {
        icon: <Package className="h-4 w-4" />,
        title: 'Picker Assignment & Timestamps',
        color: 'var(--amber)',
        bg: 'var(--amber-glow)',
        items: [
          'Assign a picker to each booking from the full user list (drivers shown first)',
          'Pick Start timestamp auto-recorded on booking creation',
          'Pick Finish timestamp recorded when picking is marked complete',
          'Weight field is now optional at booking (update later from Terminal)',
        ],
      },
      {
        icon: <Truck className="h-4 w-4" />,
        title: 'Terminal — Weight Lookup & Update',
        color: 'var(--blue)',
        bg: 'var(--blue-dim)',
        items: [
          'Search any booking by business name directly in Terminal',
          'Update weight after palletisation without editing the booking',
        ],
      },
      {
        icon: <CalendarRange className="h-4 w-4" />,
        title: 'Date Range Picker on Deliveries',
        color: 'var(--green)',
        bg: 'var(--green-dim)',
        items: [
          'New From / To date inputs alongside the existing period filter',
          'Delivery dates now use UK timezone — today\'s deliveries always show correctly',
        ],
      },
      {
        icon: <Sparkles className="h-4 w-4" />,
        title: 'Route Capacity Heatmap',
        color: 'var(--amber)',
        bg: 'var(--amber-glow)',
        items: [
          'Weekly Planner route cells show green/amber/red background based on capacity',
          'Mini capacity progress bar per route for quick visual scanning',
        ],
      },
      {
        icon: <Layers className="h-4 w-4" />,
        title: 'Backend Modularisation & Performance',
        color: 'var(--text-muted)',
        bg: 'var(--bg-raised)',
        items: [
          'server.py split from 3,600 lines into 11 modular route files',
          'N+1 database queries eliminated, bulk writes for auto-plan',
          'AI API keys encrypted at rest (Fernet), overweight auto-reconciliation',
        ],
      },
    ],
  },
  {
    version: 'v2.0.0',
    date: 'April 2026',
    tag: '',
    summary: 'RouteOps module launched — weekly planner, bookings, terminal, route config, audit trail, AI assistant.',
    sections: [
      {
        icon: <Sparkles className="h-4 w-4" />,
        title: 'RouteOps Module',
        color: 'var(--amber)',
        bg: 'var(--amber-glow)',
        items: [
          'Complete 6-page logistics dashboard: Weekly Planner, Bookings, Terminal, Route Config, Audit Trail, AI Assistant',
          'Auto-planning based on payload weight and postcodes',
          'Immutable audit trail for all mutating actions',
          'Multi-LLM AI Assistant (OpenAI, Anthropic, Gemini) with live system context',
          'JSON Backup & Restore for RouteOps data',
          'Settings panel with API key management',
        ],
      },
    ],
  },
  {
    version: 'v1.5.0',
    date: 'January 2026',
    tag: '',
    summary: 'Deliveries performance, calendar picker, bulk delete, backup system, password reset.',
    sections: [
      {
        icon: <Rocket className="h-4 w-4" />,
        title: 'Deliveries Page — Performance Boost',
        color: 'var(--green)',
        bg: 'var(--green-dim)',
        items: [
          'Default filter changed from "All Time" to "Today" (instant load)',
          'Server-side filtering — no more loading 500+ records',
          'Deliveries sorted most recent first',
        ],
      },
      {
        icon: <Calendar className="h-4 w-4" />,
        title: 'Calendar Date Picker',
        color: 'var(--blue)',
        bg: 'var(--blue-dim)',
        items: [
          'Pick a specific date with calendar popup',
          'Dynamic page title showing selected date',
          'Clear button resets view to period filter',
        ],
      },
      {
        icon: <Trash2 className="h-4 w-4" />,
        title: 'Bulk Delete (Admin Only)',
        color: 'var(--red)',
        bg: 'var(--red-dim)',
        items: [
          'Multi-select checkboxes for deliveries',
          'Select All option, selection banner, highlighted rows',
          'Only deletable statuses: Draft, Pending, Waiting for Driver, Overweight',
        ],
      },
      {
        icon: <AlertTriangle className="h-4 w-4" />,
        title: 'Admin Delete for Overweight',
        color: 'var(--amber)',
        bg: 'var(--amber-glow)',
        items: [
          'Admin-only delete for overweight deliveries with confirmation dialog',
        ],
      },
      {
        icon: <Shield className="h-4 w-4" />,
        title: 'Backup System',
        color: 'var(--text-muted)',
        bg: 'var(--bg-raised)',
        items: [
          'Daily backups at 6:00 PM UK GMT, 60-day retention',
          'Auto-delete old backups, expiry warning notifications',
        ],
      },
      {
        icon: <BarChart3 className="h-4 w-4" />,
        title: 'Dashboard Stats & Export',
        color: 'var(--blue)',
        bg: 'var(--blue-dim)',
        items: [
          'Daily, weekly, monthly, yearly delivery counters',
          'Export delivery data for reporting',
        ],
      },
      {
        icon: <KeyRound className="h-4 w-4" />,
        title: 'Password Reset',
        color: 'var(--text-muted)',
        bg: 'var(--bg-raised)',
        items: [
          'Recovery code based password reset',
        ],
      },
    ],
  },
];

export default function Changelog() {
  const [expanded, setExpanded] = useState(RELEASES[0].version);

  const toggle = (v) => setExpanded(expanded === v ? null : v);

  return (
    <div data-testid="routeops-changelog-page">
      <div className="flex items-center gap-3 pb-3 mb-6 border-b" style={{ borderColor: 'var(--border-color)' }}>
        <ScrollText className="h-5 w-5" style={{ color: 'var(--amber)' }} />
        <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)', fontFamily: 'var(--fd)' }}>Changelog</h1>
      </div>

      <div className="space-y-4 max-w-3xl">
        {RELEASES.map((release) => {
          const isOpen = expanded === release.version;
          return (
            <Card key={release.version}>
              <button
                onClick={() => toggle(release.version)}
                className="w-full text-left p-5 flex items-center justify-between"
                data-testid={`changelog-${release.version}`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-lg font-bold" style={{ color: 'var(--text-primary)', fontFamily: 'var(--fm)' }}>
                    {release.version}
                  </span>
                  {release.tag === 'latest' && (
                    <Badge className="text-[10px] uppercase" style={{ background: 'var(--green)', color: '#fff' }}>Latest</Badge>
                  )}
                  <span className="text-xs" style={{ color: 'var(--text-muted)', fontFamily: 'var(--fm)' }}>{release.date}</span>
                </div>
                {isOpen ? (
                  <ChevronDown className="h-5 w-5" style={{ color: 'var(--text-muted)' }} />
                ) : (
                  <ChevronRight className="h-5 w-5" style={{ color: 'var(--text-muted)' }} />
                )}
              </button>

              {!isOpen && (
                <div className="px-5 pb-4 -mt-2">
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{release.summary}</p>
                </div>
              )}

              {isOpen && (
                <CardContent className="pt-0 px-5 pb-5">
                  <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>{release.summary}</p>
                  <div className="space-y-3">
                    {release.sections.map((s, i) => (
                      <div key={i} className="rounded-lg overflow-hidden" style={{ border: '1px solid var(--border-color)' }}>
                        <div className="flex items-center gap-2 px-3 py-2" style={{ background: s.bg }}>
                          <span style={{ color: s.color }}>{s.icon}</span>
                          <span className="text-sm font-bold" style={{ color: s.color, fontFamily: 'var(--fd)', letterSpacing: '0.02em' }}>{s.title}</span>
                        </div>
                        <div className="px-3 py-2.5" style={{ background: 'var(--bg-raised)' }}>
                          <ul className="space-y-1">
                            {s.items.map((item, j) => (
                              <li key={j} className="flex items-start gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                                <ChevronRight className="h-3.5 w-3.5 mt-0.5 shrink-0" style={{ color: 'var(--text-muted)' }} />
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
